import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";

import { clx } from "../../../utils/Helpers";
import "./_UIBarChart.scss";

/**
 * Dependency-free vertical bar chart (CSS bars).
 *
 * `data`: `[{ id, label, value, valueLabel?, color? }]` — `valueLabel` is the
 * text shown above the bar (defaults to `value`), `valueSuffix` an optional
 * unit after it in its own span; `color` overrides the fill
 * for that one bar (e.g. to highlight or mute it). Bar heights are relative to
 * `maxValue`, or the largest value when it is not given.
 */
const UIBarChart = ({ data, maxValue, height, ariaLabel, className }) => {
  const bars = data.filter(
    (item) => item.value != null && !Number.isNaN(Number(item.value)),
  );
  const max =
    maxValue ?? (Math.max(0, ...bars.map((item) => Number(item.value))) || 1);

  const rootStyle =
    height != null
      ? {
          "--ui-bar-chart-height":
            typeof height === "number" ? `${height}px` : height,
        }
      : undefined;

  return (
    <div
      className={clx("ui-bar-chart", className)}
      role="img"
      aria-label={ariaLabel}
      style={rootStyle}
    >
      {bars.map((item) => {
        const ratio = Math.max(0, Math.min(1, Number(item.value) / max));
        const shown = item.valueLabel != null ? item.valueLabel : item.value;
        const suffix = item.valueSuffix || "";

        return (
          <div className="ui-bar-chart__col" key={item.id}>
            <span className="ui-bar-chart__stack">
              <span className="ui-bar-chart__value">
                <span>{shown}</span>
                {suffix && <span>{suffix}</span>}
              </span>
              <Tooltip title={`${item.label}: ${shown}${suffix}`} placement="top" arrow>
                <span
                  className="ui-bar-chart__bar"
                  style={{
                    "--ui-bar-chart-ratio": ratio,
                    background: item.color || undefined,
                    borderColor: item.color || undefined,
                  }}
                />
              </Tooltip>
            </span>
            <Tooltip title={item.label} placement="top" arrow>
              <span className="ui-bar-chart__label">{item.label}</span>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

UIBarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.number,
      valueLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      // Rendered in its own span after the value (e.g. the "%"), so it isn't
      // pulled into RTL bidi reordering with the number.
      valueSuffix: PropTypes.string,
      color: PropTypes.string,
    }),
  ).isRequired,
  maxValue: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

UIBarChart.defaultProps = {
  maxValue: undefined,
  height: undefined,
  ariaLabel: "Bar chart",
  className: "",
};

export default UIBarChart;
